import { apiService } from "../../api/services/apiService";
import {
  AppKeyStatus,
  EntityStatus,
  GatewaySkuStatus,
  GatewayType,
  OperationalErrorCategory,
  RouteAuthMechanism,
  DataSensitivity,
} from "../../types";
import { GATEWAY_ENV_OPTIONS } from "../gatewaySkuEnv";

export interface BulkRowResult {
  label: string;
  success: boolean;
  error?: string;
}

export interface BulkEntityConfig {
  entityLabel: string;
  acceptedExtensions: string;
  templateJson: string;
  validateRow: (row: Record<string, unknown>, index: number) => string | null;
  createRow: (
    row: Record<string, unknown>,
    tenantId: string,
  ) => Promise<void>;
}

function str(row: Record<string, unknown>, key: string): string {
  const v = row[key];
  return v == null ? "" : String(v).trim();
}

function statusOrDefault(row: Record<string, unknown>): EntityStatus {
  const s = str(row, "status").toUpperCase();
  if (Object.values(EntityStatus).includes(s as EntityStatus)) {
    return s as EntityStatus;
  }
  return EntityStatus.ACTIVE;
}

export const FAQ_BULK_CONFIG: BulkEntityConfig = {
  entityLabel: "FAQ",
  acceptedExtensions: ".json,.csv",
  templateJson: JSON.stringify(
    [
      {
        name: "Sample FAQ",
        description: "Optional description",
        question: "What is this?",
        answer: "An example FAQ.",
        status: "ACTIVE",
      },
    ],
    null,
    2,
  ),
  validateRow: (row, i) => {
    if (!str(row, "name")) return `Row ${i + 1}: name is required`;
    if (!str(row, "question")) return `Row ${i + 1}: question is required`;
    if (!str(row, "answer")) return `Row ${i + 1}: answer is required`;
    return null;
  },
  createRow: async (row, tenantId) => {
    await apiService.createFaq({
      tenantId,
      name: str(row, "name"),
      description: str(row, "description") || undefined,
      question: str(row, "question"),
      answer: str(row, "answer"),
      status: statusOrDefault(row),
    });
  },
};

export const LINK_BULK_CONFIG: BulkEntityConfig = {
  entityLabel: "Link",
  acceptedExtensions: ".json,.csv",
  templateJson: JSON.stringify(
    [
      {
        name: "Developer Portal",
        url: "https://example.com/docs",
        description: "API documentation and resources",
        status: "ACTIVE",
      },
    ],
    null,
    2,
  ),
  validateRow: (row, i) => {
    if (!str(row, "name")) return `Row ${i + 1}: name is required`;
    return null;
  },
  createRow: async (row, tenantId) => {
    await apiService.createLink({
      tenantId,
      name: str(row, "name"),
      entityClass: str(row, "url") || undefined,
      description: str(row, "description") || undefined,
      status: statusOrDefault(row),
    });
  },
};

export const DOCUMENT_BULK_CONFIG: BulkEntityConfig = {
  entityLabel: "Document",
  acceptedExtensions: ".json,.csv",
  templateJson: JSON.stringify(
    [
      {
        name: "Partner JWT restore",
        entityClass: "runbook",
        description: "Restore steps after TOKEN_EXPIRED",
        content: "# Restore partner JWT API\n\n1. Confirm token expiry.\n2. Re-issue credentials.",
        status: "ACTIVE",
      },
    ],
    null,
    2,
  ),
  validateRow: (row, i) => {
    if (!str(row, "name")) return `Row ${i + 1}: name is required`;
    const entityClass = str(row, "entityClass");
    if (
      entityClass &&
      !["sop", "runbook", "help-guide"].includes(entityClass)
    ) {
      return `Row ${i + 1}: entityClass must be sop, runbook, or help-guide`;
    }
    return null;
  },
  createRow: async (row, tenantId) => {
    await apiService.createDocument({
      tenantId,
      name: str(row, "name"),
      description: str(row, "description") || undefined,
      entityClass: str(row, "entityClass") || undefined,
      content: str(row, "content") || undefined,
      status: statusOrDefault(row),
    });
  },
};

export const CATEGORY_BULK_CONFIG: BulkEntityConfig = {
  entityLabel: "Category",
  acceptedExtensions: ".json,.csv",
  templateJson: JSON.stringify(
    [{ name: "Sample Category", status: "ACTIVE" }],
    null,
    2,
  ),
  validateRow: (row, i) => {
    if (!str(row, "name")) return `Row ${i + 1}: name is required`;
    return null;
  },
  createRow: async (row, tenantId) => {
    await apiService.createCategory({
      tenantId,
      name: str(row, "name"),
      status: statusOrDefault(row),
    });
  },
};

function parseBool(value: unknown): boolean {
  if (typeof value === "boolean") return value;
  const s = String(value).toLowerCase();
  return s === "true" || s === "yes" || s === "1";
}

function parseUuidList(row: Record<string, unknown>, key: string): string[] {
  const v = row[key];
  if (Array.isArray(v)) return v.map(String);
  if (typeof v === "string" && v.trim()) {
    return v.split(",").map((s) => s.trim()).filter(Boolean);
  }
  return [];
}

function parseStringList(row: Record<string, unknown>, key: string): string[] {
  const v = row[key];
  if (Array.isArray(v)) {
    return v.map(String).map((s) => s.trim()).filter(Boolean);
  }
  if (typeof v === "string" && v.trim()) {
    return v.split(",").map((s) => s.trim()).filter(Boolean);
  }
  return [];
}

function categoryOrUndefined(
  row: Record<string, unknown>,
): OperationalErrorCategory | undefined {
  const c = str(row, "category").toUpperCase();
  if (!c) return undefined;
  if (Object.values(OperationalErrorCategory).includes(c as OperationalErrorCategory)) {
    return c as OperationalErrorCategory;
  }
  return undefined;
}

function gatewayTypeFromRow(row: Record<string, unknown>): GatewayType | null {
  const raw = str(row, "gatewayType").toUpperCase().replace(/\s+/g, "_");
  if (!raw) return null;
  const normalized = raw.startsWith("APIGEE_") ? raw : `APIGEE_${raw}`;
  if (Object.values(GatewayType).includes(normalized as GatewayType)) {
    return normalized as GatewayType;
  }
  const alias: Record<string, GatewayType> = {
    EDGE: GatewayType.APIGEE_EDGE,
    OPDK: GatewayType.APIGEE_OPDK,
    HYBRID: GatewayType.APIGEE_HYBRID,
    X: GatewayType.APIGEE_X,
  };
  return alias[raw] ?? null;
}

function gatewaySkuStatusOrDefault(row: Record<string, unknown>): GatewaySkuStatus {
  const s = str(row, "skuStatus").toUpperCase();
  if (Object.values(GatewaySkuStatus).includes(s as GatewaySkuStatus)) {
    return s as GatewaySkuStatus;
  }
  return GatewaySkuStatus.ACTIVE;
}

const ALLOWED_GATEWAY_ENVS = new Set(
  GATEWAY_ENV_OPTIONS.map((e) => e.toLowerCase()),
);

export const GATEWAY_SKU_BULK_CONFIG: BulkEntityConfig = {
  entityLabel: "Gateway SKU",
  acceptedExtensions: ".json",
  templateJson: JSON.stringify(
    [
      {
        gatewayProductIdentifier: "prod-api-001",
        gatewayProductName: "Payments API",
        gatewayCommonName: "Payments API (Dev)",
        gatewayId: "kong-gateway-west",
        gatewayType: "APIGEE_EDGE",
        envName: "dev",
        skuStatus: "ACTIVE",
        description: "Optional",
      },
    ],
    null,
    2,
  ),
  validateRow: (row, i) => {
    const prefix = `Row ${i + 1}`;
    if (!str(row, "gatewayProductIdentifier")) {
      return `${prefix}: gatewayProductIdentifier is required`;
    }
    if (!str(row, "gatewayProductName")) {
      return `${prefix}: gatewayProductName is required`;
    }
    if (!str(row, "gatewayCommonName")) {
      return `${prefix}: gatewayCommonName is required`;
    }
    if (!str(row, "gatewayId")) {
      return `${prefix}: gatewayId is required`;
    }
    if (!gatewayTypeFromRow(row)) {
      return `${prefix}: gatewayType is required (${Object.values(GatewayType).join(", ")})`;
    }
    const env = str(row, "envName").toLowerCase();
    if (!env) {
      return `${prefix}: envName is required`;
    }
    if (!ALLOWED_GATEWAY_ENVS.has(env)) {
      return `${prefix}: envName must be one of: ${GATEWAY_ENV_OPTIONS.join(", ")}`;
    }
    const status = str(row, "skuStatus").toUpperCase();
    if (
      status &&
      !Object.values(GatewaySkuStatus).includes(status as GatewaySkuStatus)
    ) {
      return `${prefix}: invalid skuStatus (${status})`;
    }
    return null;
  },
  createRow: async (row, tenantId) => {
    const gatewayType = gatewayTypeFromRow(row);
    if (!gatewayType) {
      throw new Error("gatewayType is required");
    }
    await apiService.createGatewaySku({
      tenantId,
      gatewayProductIdentifier: str(row, "gatewayProductIdentifier"),
      gatewayProductName: str(row, "gatewayProductName"),
      gatewayCommonName: str(row, "gatewayCommonName"),
      gatewayId: str(row, "gatewayId"),
      gatewayType,
      envName: str(row, "envName").toLowerCase(),
      description: str(row, "description") || undefined,
      skuStatus: gatewaySkuStatusOrDefault(row),
    });
  },
};

export const PRODUCT_BULK_CONFIG: BulkEntityConfig = {
  entityLabel: "Product",
  acceptedExtensions: ".json",
  templateJson: JSON.stringify(
    [
      {
        name: "Sample Product",
        description: "Optional",
        status: "DRAFT",
        approvalNeeded: false,
        apiUuids: [],
        faqUuids: [],
        linkUuids: [],
        documentUuids: [],
        categoryUuids: [],
      },
    ],
    null,
    2,
  ),
  validateRow: (row, i) => {
    if (!str(row, "name")) return `Row ${i + 1}: name is required`;
    return null;
  },
  createRow: async (row, tenantId) => {
    await apiService.createProduct({
      tenantId,
      name: str(row, "name"),
      description: str(row, "description") || undefined,
      status: statusOrDefault(row),
      approvalNeeded: parseBool(row.approvalNeeded),
      apiUuids: parseUuidList(row, "apiUuids"),
      faqUuids: parseUuidList(row, "faqUuids"),
      linkUuids: parseUuidList(row, "linkUuids"),
      documentUuids: parseUuidList(row, "documentUuids"),
      categoryUuids: parseUuidList(row, "categoryUuids"),
    });
  },
};

function appKeyStatusOrDefault(row: Record<string, unknown>): AppKeyStatus {
  const s = str(row, "appKeyStatus").toUpperCase();
  if (Object.values(AppKeyStatus).includes(s as AppKeyStatus)) {
    return s as AppKeyStatus;
  }
  return AppKeyStatus.ACTIVE;
}

function optionalIsoDate(row: Record<string, unknown>, key: string): string | undefined {
  const value = str(row, key);
  if (!value) return undefined;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) {
    throw new Error(`Invalid date for ${key}: ${value}`);
  }
  return d.toISOString();
}

export const OPERATIONAL_ERROR_BULK_CONFIG: BulkEntityConfig = {
  entityLabel: "Operational error",
  acceptedExtensions: ".json,.csv",
  templateJson: JSON.stringify(
    [
      {
        code: "ERR_AUTH_001",
        name: "ERR_AUTH_001",
        message: "Invalid API key",
        description: "The provided API key is invalid or expired",
        remediation: "Verify the key in the developer portal and rotate if needed",
        httpStatus: 401,
        category: "AUTHENTICATION",
        status: "ACTIVE",
        errorStrings: ["invalid api key", "key expired"],
        productUuids: [],
        apiUuids: [],
      },
    ],
    null,
    2,
  ),
  validateRow: (row, i) => {
    if (!str(row, "code") && !str(row, "message") && !str(row, "name")) {
      return `Row ${i + 1}: code, message, or name is required`;
    }
    const category = str(row, "category");
    if (
      category &&
      !Object.values(OperationalErrorCategory).includes(
        category.toUpperCase() as OperationalErrorCategory,
      )
    ) {
      return `Row ${i + 1}: invalid category (${category})`;
    }
    const httpStatus = row.httpStatus;
    if (httpStatus != null && httpStatus !== "" && Number.isNaN(Number(httpStatus))) {
      return `Row ${i + 1}: invalid httpStatus`;
    }
    return null;
  },
  createRow: async (row, tenantId) => {
    const code = str(row, "code");
    const message = str(row, "message");
    const name = str(row, "name") || code || message || "Operational error";
    await apiService.createOperationalError({
      tenantId,
      name,
      code: code || undefined,
      message: message || undefined,
      description: str(row, "description") || undefined,
      remediation: str(row, "remediation") || undefined,
      httpStatus:
        row.httpStatus != null && row.httpStatus !== ""
          ? Number(row.httpStatus)
          : undefined,
      category: categoryOrUndefined(row),
      status: statusOrDefault(row),
      errorStrings: parseStringList(row, "errorStrings"),
      productUuids: parseUuidList(row, "productUuids"),
      apiUuids: parseUuidList(row, "apiUuids"),
    });
  },
};

export const APP_KYC_BULK_CONFIG: BulkEntityConfig = {
  entityLabel: "API Key",
  acceptedExtensions: ".json,.csv",
  templateJson: JSON.stringify(
    [
      {
        appKeyId: "sample-app-key-001",
        providerGatewayType: "Kong",
        providerGatewayIdentifier: "prod-gateway",
        appKeyStatus: "ACTIVE",
        description: "Sample API key onboarding record",
        keyCreatedOn: "2025-01-15T10:00:00.000Z",
        keyExpiresOn: "2026-01-15T10:00:00.000Z",
        contactName: "Jane Doe",
        contactEmail: "jane@example.com",
        contactPhone: "+1-555-0100",
        contactCompanyName: "Example Corp",
        contactCompanyWebsite: "https://example.com",
        bmContactName: "Account Manager",
        bmContactEmail: "am@example.com",
        itSupportName: "IT Support",
        itSupportEmail: "support@example.com",
      },
    ],
    null,
    2,
  ),
  validateRow: (row, i) => {
    if (!str(row, "appKeyId")) return `Row ${i + 1}: appKeyId is required`;
    if (!str(row, "providerGatewayType")) {
      return `Row ${i + 1}: providerGatewayType is required`;
    }
    if (!str(row, "providerGatewayIdentifier")) {
      return `Row ${i + 1}: providerGatewayIdentifier is required`;
    }
    const status = str(row, "appKeyStatus").toUpperCase();
    if (status && !Object.values(AppKeyStatus).includes(status as AppKeyStatus)) {
      return `Row ${i + 1}: invalid appKeyStatus (${status})`;
    }
    for (const dateKey of ["keyCreatedOn", "keyExpiresOn"] as const) {
      const value = str(row, dateKey);
      if (value && Number.isNaN(new Date(value).getTime())) {
        return `Row ${i + 1}: invalid ${dateKey}`;
      }
    }
    return null;
  },
  createRow: async (row, tenantId) => {
    await apiService.createAppKyc({
      tenantId,
      appKeyId: str(row, "appKeyId"),
      providerGatewayType: str(row, "providerGatewayType"),
      providerGatewayIdentifier: str(row, "providerGatewayIdentifier"),
      appKeyStatus: appKeyStatusOrDefault(row),
      description: str(row, "description") || undefined,
      keyCreatedOn: optionalIsoDate(row, "keyCreatedOn"),
      keyExpiresOn: optionalIsoDate(row, "keyExpiresOn"),
      contactName: str(row, "contactName") || undefined,
      contactEmail: str(row, "contactEmail") || undefined,
      contactPhone: str(row, "contactPhone") || undefined,
      contactCompanyName: str(row, "contactCompanyName") || undefined,
      contactCompanyWebsite: str(row, "contactCompanyWebsite") || undefined,
      bmContactName: str(row, "bmContactName") || undefined,
      bmContactEmail: str(row, "bmContactEmail") || undefined,
      itSupportName: str(row, "itSupportName") || undefined,
      itSupportEmail: str(row, "itSupportEmail") || undefined,
    });
  },
};

export const GATEWAY_ROUTE_BULK_CONFIG: BulkEntityConfig = {
  entityLabel: "Gateway route",
  acceptedExtensions: ".json",
  templateJson: JSON.stringify(
    [
      {
        basePath: "/foo",
        upstreamServer: "service-foo",
        upstreamPath: "/api/v1/foo",
        envName: "dev",
        gatewayId: "my-gateway",
        gatewayAuthMechanism: "OAUTH2",
        upstreamAuthMechanism: "BASIC",
        status: "ACTIVE",
        purpose: "Example route",
        dataSensitivity: "INTERNAL",
      },
    ],
    null,
    2,
  ),
  validateRow: (row, index) => {
    const prefix = `Row ${index + 1}`;
    if (!str(row, "basePath")) return `${prefix}: basePath is required`;
    if (!str(row, "upstreamServer")) return `${prefix}: upstreamServer is required`;
    if (!str(row, "upstreamPath")) return `${prefix}: upstreamPath is required`;
    if (!str(row, "envName")) return `${prefix}: envName is required`;
    const env = str(row, "envName").toLowerCase();
    if (!ALLOWED_GATEWAY_ENVS.has(env)) {
      return `${prefix}: envName must be one of: ${GATEWAY_ENV_OPTIONS.join(", ")}`;
    }
    if (!str(row, "gatewayId") && !str(row, "gatewaySkuUuid")) {
      return `${prefix}: gatewayId or gatewaySkuUuid is required`;
    }
    const gAuth = str(row, "gatewayAuthMechanism").toUpperCase();
    if (!gAuth || !Object.values(RouteAuthMechanism).includes(gAuth as RouteAuthMechanism)) {
      return `${prefix}: invalid gatewayAuthMechanism`;
    }
    const uAuth = str(row, "upstreamAuthMechanism").toUpperCase();
    if (!uAuth || !Object.values(RouteAuthMechanism).includes(uAuth as RouteAuthMechanism)) {
      return `${prefix}: invalid upstreamAuthMechanism`;
    }
    const sens = str(row, "dataSensitivity").toUpperCase();
    if (sens && !Object.values(DataSensitivity).includes(sens as DataSensitivity)) {
      return `${prefix}: invalid dataSensitivity`;
    }
    return null;
  },
  createRow: async (row, tenantId) => {
    await apiService.createGatewayRoute({
      tenantId,
      status: statusOrDefault(row),
      basePath: str(row, "basePath"),
      upstreamServer: str(row, "upstreamServer"),
      upstreamPath: str(row, "upstreamPath"),
      envName: str(row, "envName").toLowerCase(),
      gatewayId: str(row, "gatewayId") || undefined,
      gatewaySkuUuid: str(row, "gatewaySkuUuid") || undefined,
      gatewayAuthMechanism: str(row, "gatewayAuthMechanism").toUpperCase() as RouteAuthMechanism,
      upstreamAuthMechanism: str(row, "upstreamAuthMechanism").toUpperCase() as RouteAuthMechanism,
      name: str(row, "name") || undefined,
      description: str(row, "description") || undefined,
      gatewayAuthNotes: str(row, "gatewayAuthNotes") || undefined,
      upstreamAuthNotes: str(row, "upstreamAuthNotes") || undefined,
      purpose: str(row, "purpose") || undefined,
      dataSensitivity: str(row, "dataSensitivity")
        ? (str(row, "dataSensitivity").toUpperCase() as DataSensitivity)
        : undefined,
      notes: str(row, "notes") || undefined,
    });
  },
};
